import { CalculatedRole } from '@viapeople/api/src/shared/shared.types'
import { Button, Select, Text, ViewEvaluationIcon } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../../../admin/src/components/GenericTable'
import { formatStepName } from '../../utils/helpers'
import { useToast } from '../../utils/ToastContext'
import type { CycleStepType } from '../cycles/cycle-execution/types'
import { useTaskRouter } from '../cycles/cycle-execution/useTaskRouter'
import { AddFilters } from './AddFilters'
import type { Evaluation, SearchEvaluationStoreInstance, SearchQuery } from './store'

type Columns = Column<Evaluation>[]

interface Props {
	store: SearchEvaluationStoreInstance
}
const MULTI_EVALUATOR_STEPS = ['group_evaluation', 'unsolicited_evaluation', 'manager_evaluation', 'review'] as const
export function SearchEvaluationPage({ store }: Props) {
	const { evaluations, hasReviewStep, searchQuery, error, initialize, setSearchQuery, clearError } = store()
	const [isAddFiltersOpen, setIsAddFiltersOpen] = useState(false)
	const [isNavigating, setIsNavigating] = useState(false)
	const navigate = useNavigate()
	const { cycleId } = useParams()
	const { handleTaskRouting } = useTaskRouter()
	const [cycleStep, setCycleStep] = useState({
		evaluationId: '',
		taskId: '',
		cycleStepType: '' as CycleStepType,
	})
	const toast = useToast()

	useEffect(() => {
		if (error) {
			toast.showToast(error, 'error')
			clearError()
		}
	}, [error, toast, clearError])

	useEffect(() => {
		if (cycleId) {
			initialize(cycleId)
		}
	}, [cycleId, initialize])

	const columns: Columns = [
		{
			field: 'firstName',
			label: 'First Name',
			colSpan: 2,
			sortable: true,
			render: (evaluation: Evaluation) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{evaluation.firstName}
				</Text>
			),
		},
		{
			field: 'lastName',
			label: 'Last Name',
			colSpan: 2,
			sortable: true,
			render: (evaluation: Evaluation) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{evaluation.lastName}
				</Text>
			),
		},
		{
			field: 'reviewerCount',
			label: 'Raters',
			colSpan: 1,
			sortable: true,
			render: (evaluation: Evaluation) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{evaluation.reviewerCount}
				</Text>
			),
		},
		{
			field: 'tasks',
			label: 'Status',
			colSpan: 1,
			sortable: false,
			render: (evaluation: Evaluation) => {
				const options = evaluation.tasks.map((task: Evaluation['tasks'][number]) => {
					const stepName = formatStepName(task.cycleStepDisplayName)

					// For steps with multiple evaluators, show evaluator name
					const isMultiEvaluator = (MULTI_EVALUATOR_STEPS as readonly string[]).includes(
						task.cycleStepDisplayName.toLowerCase(),
					)

					let label = stepName
					if (isMultiEvaluator) {
						// Handle anonymization: if firstName/lastName are null and role is 'unsolicited',
						// it means the evaluator is anonymized for non-admins
						if (task.evaluatorFirstName && task.evaluatorLastName) {
							const evaluatorName = `${task.evaluatorFirstName} ${task.evaluatorLastName}`
							// Show role if available (e.g., "Primary Manager", "Secondary Manager", "Rater")
							const roleLabel = task.calculatedRole ? formatStepName(task.calculatedRole) : ''
							// If role is 'unsolicited' and names are present, it means cycle admin is viewing
							// an anonymized evaluation (show "(anonymized)" label)
							const isAnonymizedUnsolicited = task.calculatedRole === CalculatedRole.UNSOLICITED
							const anonymizedLabel = isAnonymizedUnsolicited ? ' (anonymized)' : ''
							label = `${stepName} - ${evaluatorName}${anonymizedLabel}${roleLabel ? ` (${roleLabel})` : ''}`
						} else if (task.calculatedRole === CalculatedRole.UNSOLICITED) {
							// Non-admin viewing anonymized unsolicited evaluation - names are null
							label = `${stepName} - Anonymous`
						} else if (task.calculatedRole) {
							// Fallback: show role if available (for other roles without names)
							const roleLabel = formatStepName(task.calculatedRole)
							label = `${stepName} - ${roleLabel}`
						}
					}

					return {
						value: task.id,
						label,
					}
				})

				return (
					<Select
						options={options}
						value={cycleStep.taskId}
						onChange={(value: string) => {
							const selectedTask = evaluation.tasks.find((t) => t.id === value)
							setCycleStep({
								evaluationId: evaluation.evaluationId,
								taskId: value,
								cycleStepType: (selectedTask?.cycleStepDisplayName ?? '') as CycleStepType,
							})
						}}
					/>
				)
			},
		},
		{
			field: 'actions',
			label: 'Actions',
			colSpan: 2,
			sortable: false,
			render: (evaluation: Evaluation) => {
				// Check if a Review task exists for this evaluation
				const hasReviewTask = evaluation.tasks?.some((task) => task.cycleStepDisplayName === 'review')

				return (
					<div className='flex gap-2'>
						<Button
							disabled={cycleStep?.evaluationId !== evaluation.evaluationId || isNavigating}
							variant={'secondary'}
							onClick={() => {
								setIsNavigating(true)
								handleTaskRouting(cycleStep.taskId, cycleStep.cycleStepType, cycleId)
							}}>
							{isNavigating && cycleStep?.evaluationId === evaluation.evaluationId ? 'Loading...' : 'View form'}
						</Button>
						{hasReviewStep && (
							<Link
								to={`/cycle/${cycleId}/evaluations/${evaluation.evaluationId}/review-preview`}
								className={`flex flex-row items-center justify-center gap-x-1 text-base rounded-lg font-medium transition-colors focus:outline-none whitespace-nowrap px-3 py-2 text-sm ${
									hasReviewTask
										? 'cursor-not-allowed text-neutral-300 bg-neutral-200 pointer-events-none'
										: 'cursor-pointer bg-neutral-0 text-neutral-800 hover:bg-neutral-200'
								}`}
								onClick={(e) => {
									if (hasReviewTask) {
										e.preventDefault()
									}
								}}>
								Preview Review Form
							</Link>
						)}
					</div>
				)
			},
		},
	]

	return (
		<main className='w-full p-6'>
			<div className='mb-6'>
				<Text variant='h3' weight='semibold'>
					Search Evaluation
				</Text>
				<Button variant={'tertiary'} onClick={() => navigate(`/dashboard/${cycleId}/tasks`)}>
					&lt; Back to cycle
				</Button>
			</div>

			<div className='border border-neutral-200 bg-neutral-0 rounded-lg shadow-xl'>
				<GenericTable<Evaluation, SearchQuery>
					data={evaluations}
					columns={columns}
					idField='evaluationId'
					showSearch
					searchValue={searchQuery?.search ?? ''}
					onSearchChange={setSearchQuery}
					showPagination
					emptyState={{
						icon: ViewEvaluationIcon,
						title: 'No evaluations found',
					}}
				/>
			</div>

			<AddFilters
				isOpen={isAddFiltersOpen}
				onClose={() => setIsAddFiltersOpen(false)}
				onAddFilters={() => setIsAddFiltersOpen(false)}
			/>
		</main>
	)
}
